import { Box, Button, Checkbox, Flex, Input, Stack, Tag, Text } from "@chakra-ui/react"
import { useMemo, useState } from "react"
import { sortByLabel } from "lib/sort"
import { tokenFilter } from "lib/utils"

// At most this many lines are drawn; the rest is reached through the search field
const MAX_SHOWN = 200

// A list of lines with a tick each, a search field and "Select shown" / "Clear shown" (they act on
// what the search currently shows). Used by the forms that add something to / remove it from
// several groups, locations or broadcasts at once.
// - items:   [{ id, label, hint?, tag?, locked? }] sorted by label here. `hint` is a small grey
//            text, `tag` a green tag (e.g. "x 2"); a `locked` line cannot be changed.
// - initial: Set of the ids that were ticked when the form opened
// - ticked:  Set of the ids ticked now
// - onToggle(id), onSetMany(ids, checked): the parent keeps the Sets
// - noun:    what the lines are ("locations"), for the counters
export default function TickList({ items, initial, ticked, saving, onToggle, onSetMany, noun = "items" }) {
  const [term, setTerm] = useState("")

  const sorted = useMemo(() => sortByLabel(items), [items])
  const matching = useMemo(() => sorted.filter(item => tokenFilter(`${item.label} ${item.hint || ""}`, term)), [sorted, term])
  // With an empty search the lines that were ticked when the form opened come first (then the rest,
  // each part by name). Ticking in the form does not move a line, it would jump away from the mouse.
  // Each part is cut at MAX_SHOWN separately, so a long first part cannot hide all the others.
  const parts = term.trim() ? [matching] : [matching.filter(item => initial.has(item.id)), matching.filter(item => !initial.has(item.id))]
  const shown = parts.flatMap(part => part.slice(0, MAX_SHOWN))
  const changeable = matching.filter(item => !item.locked)

  return (
    <Stack direction="column" spacing={2}>
      <Input size="sm" borderRadius="30px" placeholder="Search..." value={term} onChange={e => setTerm(e.target.value)} />
      <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
        <Text fontSize="sm" color="gray.500">
          {matching.length} / {items.length} {noun} shown, {ticked.size} ticked
        </Text>
        <Stack direction="row" spacing={1}>
          <Button size="xs" variant="ghost" colorScheme="brand" isDisabled={saving || changeable.length === 0}
            onClick={() => onSetMany(changeable.map(item => item.id), true)}>
            Select shown
          </Button>
          <Button size="xs" variant="ghost" colorScheme="brand" isDisabled={saving || !changeable.some(item => ticked.has(item.id))}
            onClick={() => onSetMany(changeable.map(item => item.id), false)}>
            Clear shown
          </Button>
        </Stack>
      </Flex>
      <Stack direction="column" spacing={1}>
        {shown.map(item => {
          const was = initial.has(item.id)
          const is = ticked.has(item.id)
          return (
            <Box key={item.id} borderWidth="1px" borderRadius="lg" px={3} py={2} bg={is !== was ? "brand.100" : (was ? "green.50" : "white")}>
              <Checkbox colorScheme="green" isChecked={is} onChange={() => onToggle(item.id)} isDisabled={saving || item.locked}>
                <Text as="span" fontSize="sm" fontWeight="medium">{item.label || "(no name)"}</Text>
              </Checkbox>
              {(item.hint || item.tag) && (
                <Stack direction="row" spacing={2} mt={1} pl={6} wrap="wrap" align="center">
                  {item.tag && <Tag size="sm" colorScheme="green" variant="subtle">{item.tag}</Tag>}
                  {item.hint && <Text fontSize="xs" color="gray.500">{item.hint}</Text>}
                </Stack>
              )}
            </Box>
          )
        })}
        {matching.length === 0 && <Text fontSize="sm" color="gray.400">Nothing found</Text>}
        {matching.length > shown.length && (
          <Text fontSize="xs" color="gray.500">{matching.length - shown.length} more, type to narrow the list</Text>
        )}
      </Stack>
    </Stack>
  )
}
